import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

/**
 * Tracking Link Service - Handles unique tracking links for influencer campaigns
 */
const trackingLinkService = {
  /**
   * Generate a unique tracking link for an influencer-campaign combination
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer user ID
   * @param {string} participantId - Campaign participant ID
   * @returns {Promise<object>} - Generated tracking link data
   */
  generateTrackingLink: async (campaignId, influencerId, participantId) => {
    try {
      // Check if a tracking link already exists for this combination
      const existingLink = await trackingLinkService.getTrackingLink(campaignId, influencerId);
      if (existingLink) {
        return existingLink;
      }

      // Generate unique tracking code (shorter and cleaner)
      const trackingCode = uuidv4().substring(0, 12);
      
      // Create tracking link data
      const trackingLinkData = {
        campaignId,
        influencerId,
        participantId,
        trackingCode,
        trackingUrl: `https://engagedmanila.com/t/${trackingCode}`,
        createdAt: serverTimestamp(),
        isActive: true,
        analytics: {
          clicks: 0,
          uniqueClicks: 0,
          conversions: 0,
          impressions: 0,
          lastClickedAt: null,
          lastImpressionAt: null
        }
      };

      // Save to Firestore
      const trackingLinksRef = collection(db, 'trackingLinks');
      const docRef = await addDoc(trackingLinksRef, trackingLinkData);

      return {
        id: docRef.id,
        ...trackingLinkData
      };
    } catch (error) {
      console.error('Error generating tracking link:', error);
      throw error;
    }
  },

  /**
   * Get tracking link for a specific campaign-influencer combination
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer user ID
   * @returns {Promise<object|null>} - Tracking link data or null if not found
   */
  getTrackingLink: async (campaignId, influencerId) => {
    try {
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(
        trackingLinksRef,
        where('campaignId', '==', campaignId),
        where('influencerId', '==', influencerId),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        ...data,
        trackingUrl: `https://engagedmanila.com/t/${data.trackingCode}`
      };
    } catch (error) {
      console.error('Error getting tracking link:', error);
      throw error;
    }
  },

  /**
   * Get tracking link by tracking code
   * @param {string} trackingCode - Tracking code
   * @returns {Promise<object|null>} - Tracking link data or null if not found
   */
  getTrackingLinkByCode: async (trackingCode) => {
    try {
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(
        trackingLinksRef,
        where('trackingCode', '==', trackingCode),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error getting tracking link by code:', error);
      throw error;
    }
  },



  /**
   * Track a click on the tracking link
   * @param {string} trackingLinkId - Tracking link ID
   * @param {object} clickData - Click data (IP, user agent, etc.)
   * @returns {Promise<void>}
   */
  trackClick: async (trackingLinkId, clickData = {}) => {
    try {
      const trackingLinkRef = doc(db, 'trackingLinks', trackingLinkId);
      const trackingLinkDoc = await getDoc(trackingLinkRef);
      
      if (!trackingLinkDoc.exists()) {
        throw new Error('Tracking link not found');
      }

      const currentData = trackingLinkDoc.data();
      const currentAnalytics = currentData.analytics || {};

      // Update analytics
      await updateDoc(trackingLinkRef, {
        'analytics.clicks': (currentAnalytics.clicks || 0) + 1,
        'analytics.lastClickedAt': serverTimestamp(),
        lastClickedAt: serverTimestamp()
      });

      // Log individual click for detailed analytics
      const clicksRef = collection(db, 'trackingClicks');
      await addDoc(clicksRef, {
        trackingLinkId,
        campaignId: currentData.campaignId,
        influencerId: currentData.influencerId,
        clickedAt: serverTimestamp(),
        ...clickData
      });

    } catch (error) {
      console.error('Error tracking click:', error);
      throw error;
    }
  },

  /**
   * Get analytics for a tracking link
   * @param {string} trackingLinkId - Tracking link ID
   * @returns {Promise<object>} - Analytics data
   */
  getTrackingAnalytics: async (trackingLinkId) => {
    try {
      const trackingLinkRef = doc(db, 'trackingLinks', trackingLinkId);
      const trackingLinkDoc = await getDoc(trackingLinkRef);
      
      if (!trackingLinkDoc.exists()) {
        throw new Error('Tracking link not found');
      }

      const data = trackingLinkDoc.data();
      return data.analytics || {
        clicks: 0,
        uniqueClicks: 0,
        conversions: 0,
        lastClickedAt: null
      };
    } catch (error) {
      console.error('Error getting tracking analytics:', error);
      throw error;
    }
  },

  /**
   * Deactivate a tracking link
   * @param {string} trackingLinkId - Tracking link ID
   * @returns {Promise<void>}
   */
  deactivateTrackingLink: async (trackingLinkId) => {
    try {
      const trackingLinkRef = doc(db, 'trackingLinks', trackingLinkId);
      await updateDoc(trackingLinkRef, {
        isActive: false,
        deactivatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deactivating tracking link:', error);
      throw error;
    }
  },

  /**
   * Get all tracking links for a campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array>} - Array of tracking links
   */
  getTrackingLinksByCampaign: async (campaignId) => {
    try {
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(
        trackingLinksRef,
        where('campaignId', '==', campaignId),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      const trackingLinks = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        trackingLinks.push({
          id: doc.id,
          ...data,
          trackingUrl: data.trackingUrl || `${window.location.origin}/t/${data.trackingCode}`
        });
      });

      return trackingLinks;
    } catch (error) {
      console.error('Error getting tracking links by campaign:', error);
      throw error;
    }
  },

  /**
   * Generate UTM-enhanced tracking URLs for different platforms
   * @param {string} trackingCode - The tracking code
   * @param {object} campaignData - Campaign information
   * @param {string} influencerHandle - Influencer's social media handle
   * @returns {object} - Platform-specific URLs with UTM parameters
   */
  generateUTMUrls: (trackingCode, campaignData = {}, influencerHandle = '') => {
    const baseUrl = `${window.location.origin}/t/${trackingCode}`;
    const campaignName = (campaignData.name || campaignData.title || 'campaign').toLowerCase().replace(/\s+/g, '_');
    
    return {
      // Base URL without UTM (for character-limited scenarios)
      base: baseUrl,
      
      // Instagram-specific URL
      instagram: `${baseUrl}?utm_source=instagram&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=post`,
      
      // Instagram Stories
      instagramStory: `${baseUrl}?utm_source=instagram&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=story`,
      
      // TikTok URL
      tiktok: `${baseUrl}?utm_source=tiktok&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=video`,
      
      // Facebook URL
      facebook: `${baseUrl}?utm_source=facebook&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=post`,
      
      // YouTube URL
      youtube: `${baseUrl}?utm_source=youtube&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=video`,
      
      // Twitter/X URL
      twitter: `${baseUrl}?utm_source=twitter&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=tweet`,
      
      // Blog/Website URL
      blog: `${baseUrl}?utm_source=blog&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=article`,
      
      // Email/Newsletter URL
      email: `${baseUrl}?utm_source=email&utm_medium=influencer&utm_campaign=${campaignName}&utm_content=${influencerHandle}&utm_term=newsletter`
    };
  },

  /**
   * Generate comprehensive tracking instructions with UTM URLs
   * @param {string} trackingCode - The tracking code
   * @param {object} campaignData - Campaign information
   * @param {string} influencerHandle - Influencer's handle
   * @returns {object} - Complete tracking instructions
   */
  generateTrackingInstructions: (trackingCode, campaignData = {}, influencerHandle = '') => {
    const utmUrls = trackingLinkService.generateUTMUrls(trackingCode, campaignData, influencerHandle);
    
    return {
      urls: utmUrls,
      instructions: {
        instagram: {
          post: {
            text: "Include this link in your post caption:",
            url: utmUrls.instagram,
            tip: "Add it naturally in your caption or as the first comment"
          },
          story: {
            text: "Use this link for Instagram Stories:",
            url: utmUrls.instagramStory,
            tip: "Add as swipe-up link or 'Link in Bio' sticker"
          },
          reel: {
            text: "For Instagram Reels, use:",
            url: utmUrls.instagram,
            tip: "Mention verbally in video and add to description"
          }
        },
        tiktok: {
          video: {
            text: "For TikTok videos:",
            url: utmUrls.tiktok,
            tip: "Mention the link verbally and add to video description"
          },
          bio: {
            text: "Update your TikTok bio temporarily:",
            url: utmUrls.base,
            tip: "Use the shorter version for bio links"
          }
        },
        facebook: {
          post: {
            text: "For Facebook posts:",
            url: utmUrls.facebook,
            tip: "Facebook will generate a preview automatically"
          },
          story: {
            text: "For Facebook Stories:",
            url: utmUrls.base,
            tip: "Use the link sticker feature"
          }
        },
        youtube: {
          description: {
            text: "Add to your YouTube video description:",
            url: utmUrls.youtube,
            tip: "Place it in the first 2 lines for better visibility"
          },
          comment: {
            text: "Pin this as your first comment:",
            url: utmUrls.base,
            tip: "Pin the comment to keep it at the top"
          }
        },
        blog: {
          article: {
            text: "For blog posts or articles:",
            url: utmUrls.blog,
            tip: "Embed as hyperlinks within your content"
          }
        },
        email: {
          newsletter: {
            text: "For email newsletters:",
            url: utmUrls.email,
            tip: "Use as call-to-action buttons or hyperlinks"
          }
        }
      },
      analytics: {
        note: "Each platform URL has unique UTM parameters for detailed attribution tracking",
        benefits: [
          "Track which platforms drive the most traffic",
          "Measure performance across different content types",
          "Optimize future campaigns based on platform data",
          "Calculate ROI per social media channel"
        ]
      }
    };
  }
};

export default trackingLinkService;
